import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, MapPin, Clock, Car, ChevronLeft } from "lucide-react";
import SEO from "../../components/SEO";
import SmartImage from "../../components/SmartImage";
import DestinationGuide, { QuickFactsExtra } from "../../components/DestinationGuide";
import { api_get, apiError } from "../../lib/api";
import useViewTracker from "../../hooks/useViewTracker";
import destinationGuides from "../../data/destinationGuides";

const DestinationDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [d, setD] = useState(null);
    const [related, setRelated] = useState([]);
    const [error, setError] = useState(null);
    useViewTracker("destination", slug);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const [dest, all] = await Promise.all([
                    api_get(`/destinations/${slug}`),
                    api_get(`/destinations`),
                ]);
                if (cancelled) return;
                setD(dest);
                setRelated(all.filter((x) => x.slug !== slug).slice(0, 3));
            } catch (err) {
                if (cancelled) return;
                setError(apiError(err));
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (error) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="text-center">
                    <p className="text-[var(--ink-soft)] text-sm">Destination not found.</p>
                    <button
                        onClick={() => navigate("/destinations")}
                        className="mt-4 hz-link text-sm"
                    >
                        ← Back to all destinations
                    </button>
                </div>
            </div>
        );
    }

    if (!d) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-[var(--ink-soft)]">
                {t("common.loading")}
            </div>
        );
    }

    const breadcrumbJsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://southtrip.in/" },
            { "@type": "ListItem", position: 2, name: "Destinations", item: "https://southtrip.in/destinations" },
            { "@type": "ListItem", position: 3, name: d.title, item: `https://southtrip.in/destinations/${d.slug}` },
        ],
    };
    /* Build guide from DB first (admin-editable), falling back to the static curated content. */
    const dbGuide = (() => {
        const has = (v) => Array.isArray(v) ? v.length > 0 : (v && typeof v === "object" ? Object.keys(v).length > 0 : !!v);
        if (!d) return null;
        const candidate = {
            quickFactsExtra: d.quickFacts || {},
            thingsToDo: (d.thingsToDo || []).slice().sort((a, b) => (a.displayOrder ?? 100) - (b.displayOrder ?? 100)),
            photoSpots: (d.photoSpots || []).slice().sort((a, b) => (a.displayOrder ?? 100) - (b.displayOrder ?? 100)),
            restaurants: d.restaurants || [],
            gulfTravellers: d.gulfTravellers || {},
            itineraries: d.itineraries || {},
            hotels: d.hotels || {},
            prices: d.prices || [],
            sectionTitles: d.sectionTitles || {},
            ctas: {
                itinerary: d.itineraryCta || {},
                gettingThere: d.gettingThereCtas || [],
            },
            meta: {
                title: d.seoTitle,
                description: d.seoDescription,
            },
        };
        const hasAny = has(candidate.thingsToDo) || has(candidate.photoSpots) || has(candidate.restaurants) || has(candidate.gulfTravellers) || has(candidate.itineraries) || has(candidate.hotels) || has(candidate.prices);
        return hasAny ? candidate : null;
    })();
    const guide = dbGuide || destinationGuides[d.slug];
    const touristJsonLd = {
        "@context": "https://schema.org",
        "@type": guide ? "TouristDestination" : "TouristAttraction",
        name: d.title,
        description: guide?.meta?.description || d.longDescription || d.shortDescription,
        image: d.imageUrl || "/default-kerala-destination.svg",
        url: `https://southtrip.in/destinations/${d.slug}`,
        address: {
            "@type": "PostalAddress",
            addressLocality: d.title,
            addressRegion: "Kerala",
            addressCountry: "IN",
        },
        ...(guide?.thingsToDo && {
            includesAttraction: guide.thingsToDo.slice(0, 5).map((it) => ({
                "@type": "TouristAttraction",
                name: it.name,
                description: it.desc,
            })),
        }),
    };

    return (
        <div data-testid="destination-detail">
            <SEO
                title={guide?.meta?.title || d.seoTitle || `${d.title} · Kerala Cab Booking · SouthTrip`}
                description={guide?.meta?.description || d.seoDescription || d.shortDescription}
                path={`/destinations/${d.slug}`}
                image={d.imageUrl}
                jsonLd={[breadcrumbJsonLd, touristJsonLd]}
                keywords={`${d.title.toLowerCase()} cab, ${d.title.toLowerCase()} taxi, kerala destination ${d.title.toLowerCase()}, kerala tour ${d.title.toLowerCase()}`}
            />

            {/* HERO */}
            <section className="relative">
                <div className="aspect-[16/9] sm:aspect-[16/7] lg:aspect-[16/6] overflow-hidden bg-[var(--bg-surface)]">
                    <SmartImage
                        src={d.imageUrl}
                        alt={d.imageAlt || d.title}
                        className="w-full h-full"
                        loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-12 text-white">
                    <Link to="/destinations" className="inline-flex items-center gap-1 text-xs sm:text-sm text-white/80 hover:text-white mb-3" data-testid="back-to-destinations">
                        <ChevronLeft className="w-4 h-4" /> All destinations
                    </Link>
                    <div className="text-xs uppercase tracking-[0.18em] text-white/85">{d.category || d.subtitle}</div>
                    <h1 className="font-hz-display text-4xl sm:text-5xl md:text-7xl tracking-tight leading-[1] mt-2 max-w-3xl">
                        {d.title}
                    </h1>
                    <p className="mt-3 text-sm sm:text-base text-white/85 max-w-2xl">{d.shortDescription}</p>
                </div>
            </section>

            {/* CONTENT */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
                <article className="lg:col-span-8">
                    <h2 className="font-hz-display text-2xl md:text-3xl tracking-tight">About {d.title}</h2>
                    <p className="text-[var(--ink-soft)] leading-relaxed mt-4 text-base">
                        {d.longDescription || d.shortDescription}
                    </p>

                    {d.bestFor && d.bestFor.length > 0 && (
                        <div className="mt-8">
                            <h3 className="font-hz-display text-xl tracking-tight">Best for</h3>
                            <ul className="flex flex-wrap gap-2 mt-3">
                                {d.bestFor.map((b, i) => (
                                    <li key={i} className="hz-pill bg-[var(--bg-surface)] text-[var(--ink-soft)]">
                                        {b}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Link to={d.buttonUrl || "/cab-booking"} className="hz-btn-primary text-sm" data-testid="dest-cta-cab">
                            {d.buttonText || `Book cab to ${d.title}`} <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <Link to="/tour-packages" className="hz-btn-ghost text-sm" data-testid="dest-cta-packages">
                            Tour packages with {d.title} <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </article>

                <aside className="lg:col-span-4">
                    <div className="hz-card bg-white border border-[var(--border)] p-6 sticky top-24">
                        <div className="text-xs uppercase tracking-wider text-[var(--ink-softer)] font-semibold">
                            Quick facts
                        </div>
                        <dl className="mt-3 space-y-3 text-sm">
                            {d.distanceDuration && (
                                <div className="flex items-start gap-3">
                                    <Car className="w-4 h-4 mt-0.5 text-[var(--ink-soft)]" />
                                    <div>
                                        <dt className="text-[var(--ink-softer)] text-xs uppercase tracking-wider">Distance / drive</dt>
                                        <dd className="text-[var(--ink)] font-medium">{d.distanceDuration}</dd>
                                    </div>
                                </div>
                            )}
                            {d.bestSeason && (
                                <div className="flex items-start gap-3">
                                    <Clock className="w-4 h-4 mt-0.5 text-[var(--ink-soft)]" />
                                    <div>
                                        <dt className="text-[var(--ink-softer)] text-xs uppercase tracking-wider">Best season</dt>
                                        <dd className="text-[var(--ink)] font-medium">{d.bestSeason}</dd>
                                    </div>
                                </div>
                            )}
                            {d.category && (
                                <div className="flex items-start gap-3">
                                    <MapPin className="w-4 h-4 mt-0.5 text-[var(--ink-soft)]" />
                                    <div>
                                        <dt className="text-[var(--ink-softer)] text-xs uppercase tracking-wider">Category</dt>
                                        <dd className="text-[var(--ink)] font-medium">{d.category}</dd>
                                    </div>
                                </div>
                            )}
                        </dl>
                        {guide?.quickFactsExtra && <QuickFactsExtra data={guide.quickFactsExtra} />}
                    </div>
                </aside>
            </section>

            {/* FULL TRAVEL GUIDE (only for cities with curated content) */}
            {guide && <DestinationGuide guide={guide} city={d.title} />}

            {/* RELATED */}
            {related.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20" data-testid="related-destinations">
                    <h2 className="font-hz-display text-2xl md:text-3xl tracking-tight mb-6">More Kerala destinations</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {related.map((r) => (
                            <Link
                                key={r.id}
                                to={`/destinations/${r.slug}`}
                                className="hz-card group overflow-hidden bg-white border border-[var(--border)]"
                                data-testid={`related-${r.slug}`}
                            >
                                <div className="aspect-[4/3] overflow-hidden bg-[var(--bg-surface)]">
                                    <SmartImage
                                        src={r.imageUrl}
                                        alt={r.imageAlt || r.title}
                                        className="w-full h-full transition duration-700 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-5">
                                    <div className="text-xs text-[var(--ink-softer)]">{r.subtitle}</div>
                                    <h3 className="font-hz-display text-xl tracking-tight mt-1">{r.title}</h3>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

export default DestinationDetail;
